import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum, boolean } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// ============================================================================
// LENDER MANAGER MODULE TABLES (8 tables) - SPRINT-4
// Lender profiles, products, pricing, fees, commissions, document checklists
// Unblocks Sprint 3 facilities.lender_id and facilities.lender_product_id FKs
// ============================================================================

// Lender Type Enum
export const lenderTypeEnum = pgEnum("lender_type", ["bank", "non_bank", "private", "smsf_lender"]);

// Lender Status Enum
export const lenderStatusEnum = pgEnum("lender_status", ["active", "inactive", "suspended"]);

// Accreditation Status Enum
export const accreditationStatusEnum = pgEnum("accreditation_status", ["active", "expired", "pending", "renewal_due"]);

// Product Category Enum
export const productCategoryEnum = pgEnum("product_category", [
  "term_loan",
  "line_of_credit",
  "overdraft",
  "equipment_finance",
  "invoice_finance",
  "trade_finance"
]);

// Lender Product Loan Type Enum (different from facility loanTypeEnum)
export const lenderLoanTypeEnum = pgEnum("lender_loan_type", [
  "residential",
  "commercial",
  "smsf",
  "construction",
  "development",
  "industrial",
  "rural"
]);

// Interest Rate Type Enum
export const interestRateTypeEnum = pgEnum("interest_rate_type", ["fixed", "variable", "split"]);

// Fee Type Enum
export const feeTypeEnum = pgEnum("fee_type", [
  "application_fee",
  "establishment_fee",
  "valuation_fee",
  "legal_fee",
  "service_fee",
  "exit_fee",
  "variation_fee",
  "discharge_fee"
]);

// Fee Frequency Enum
export const feeFrequencyEnum = pgEnum("fee_frequency", [
  "one_time",
  "monthly",
  "quarterly",
  "annual"
]);

// Amount Type Enum
export const amountTypeEnum = pgEnum("amount_type", ["fixed", "percentage", "variable"]);

// 1. Lenders Table (24 fields - NO trading_name per user change)
export const lenders = pgTable("crm_x_lenders", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Basic Info
  lenderName: varchar("lender_name", { length: 255 }).notNull(),
  lenderCode: varchar("lender_code", { length: 20 }).notNull().unique(),
  lenderType: lenderTypeEnum("lender_type").notNull(),
  status: lenderStatusEnum("status").default("active"),

  // BDM Contact
  bdmName: varchar("bdm_name", { length: 255 }),
  bdmEmail: varchar("bdm_email", { length: 255 }),
  bdmPhone: varchar("bdm_phone", { length: 20 }),
  bdmMobile: varchar("bdm_mobile", { length: 20 }),

  // Accreditation
  accreditationDate: date("accreditation_date"),
  accreditationExpiry: date("accreditation_expiry"),
  accreditationStatus: accreditationStatusEnum("accreditation_status").default("pending"),
  aggregatorId: varchar("aggregator_id", { length: 100 }),

  // Lending Appetite (product-level attributes, not lender-level)
  preferredSecurityTypes: text("preferred_security_types").array(),
  excludedIndustries: text("excluded_industries").array(),

  // Commission Defaults (D-001 fallback rates)
  defaultUpfrontRate: numeric("default_upfront_rate", { precision: 5, scale: 4 }).default("0.0000"),
  defaultTrailRate: numeric("default_trail_rate", { precision: 5, scale: 4 }).default("0.0000"),
  clawbackPeriodMonths: integer("clawback_period_months").default(24),

  // Integration
  applicationPortalUrl: varchar("application_portal_url", { length: 500 }),
  apiIntegrationEnabled: boolean("api_integration_enabled").default(false),
  notes: text("notes"),

  // Audit Fields
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// 2. Lender Products Table (15 fields)
export const lenderProducts = pgTable("crm_x_lender_products", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  lenderId: uuid("lender_id").references(() => lenders.id, { onDelete: "cascade" }).notNull(),
  productName: varchar("product_name", { length: 255 }).notNull(),
  productCode: varchar("product_code", { length: 50 }).notNull(),
  productCategory: productCategoryEnum("product_category").notNull(),
  loanType: lenderLoanTypeEnum("loan_type").notNull(),

  // Term Limits
  minTermMonths: integer("min_term_months"),
  maxTermMonths: integer("max_term_months"),

  // Interest Rate Configuration
  interestRateType: interestRateTypeEnum("interest_rate_type").default("variable"),
  baseRateSource: varchar("base_rate_source", { length: 50 }).default("RBA_cash"),
  currentBaseRate: numeric("current_base_rate", { precision: 7, scale: 4 }).default("0.0000"),

  // Commission (D-001 hierarchy)
  upfrontRate: numeric("upfront_rate", { precision: 5, scale: 4 }).default("0.0000"),
  trailRate: numeric("trail_rate", { precision: 5, scale: 4 }).default("0.0000"),

  status: lenderStatusEnum("status").default("active"),

  // Eligibility Gate Fields (GOLDEN-PATH F001)
  // Power the lender matching engine (F004)
  minDscr: numeric("min_dscr", { precision: 5, scale: 2 }),
  minIcr: numeric("min_icr", { precision: 5, scale: 2 }),
  maxDebtToEbitda: numeric("max_debt_to_ebitda", { precision: 5, scale: 2 }),
  acceptableSecurityTypes: text("acceptable_security_types").array(),
  acceptableEntityTypes: text("acceptable_entity_types").array(),
  geographicRestrictions: text("geographic_restrictions").array(),
  requiresExistingCustomer: boolean("requires_existing_customer").notNull().default(false),
  requiresFullDoc: boolean("requires_full_doc").notNull().default(true),

  // Additional Matching Fields (GOLDEN-PATH F004)
  indicativeRate: numeric("indicative_rate", { precision: 7, scale: 4 }),
  maxLvr: numeric("max_lvr", { precision: 5, scale: 2 }),
  minAmount: numeric("min_amount", { precision: 15, scale: 2 }),
  maxAmount: numeric("max_amount", { precision: 15, scale: 2 }),

  // Audit Fields
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// 3. Lender Pricing Tiers Table
export const lenderPricingTiers = pgTable("crm_x_lender_pricing_tiers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  lenderProductId: uuid("lender_product_id").references(() => lenderProducts.id, { onDelete: "cascade" }).notNull(),

  // LVR Band
  lvrMin: numeric("lvr_min", { precision: 5, scale: 2 }).notNull(),
  lvrMax: numeric("lvr_max", { precision: 5, scale: 2 }).notNull(),

  // Pricing Margin
  margin: numeric("margin", { precision: 7, scale: 4 }).notNull(),

  // Audit Fields
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// 4. Lender Fee Groups Table
export const lenderFeeGroups = pgTable("crm_x_lender_fee_groups", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  lenderId: uuid("lender_id").references(() => lenders.id, { onDelete: "cascade" }).notNull(),
  groupName: varchar("group_name", { length: 100 }).notNull(),
  description: text("description"),
  isDefault: boolean("is_default").default(false),

  // Audit Fields (only those that exist in database)
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 5. Lender Fees Table
export const lenderFees = pgTable("crm_x_lender_fees", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  feeGroupId: uuid("fee_group_id").references(() => lenderFeeGroups.id, { onDelete: "cascade" }).notNull(),
  feeType: feeTypeEnum("fee_type").notNull(),
  feeName: varchar("fee_name", { length: 100 }).notNull(),
  amountType: amountTypeEnum("amount_type").notNull(),

  // Amount Configuration
  fixedAmount: numeric("fixed_amount", { precision: 15, scale: 2 }),
  percentageAmount: numeric("percentage_amount", { precision: 5, scale: 4 }),

  description: text("description"),
  isMandatory: boolean("is_mandatory").default(false),
  feeFrequency: feeFrequencyEnum("fee_frequency").notNull().default("one_time"),

  // Audit Fields
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// 6. Lender Commissions Table
export const lenderCommissions = pgTable("crm_x_lender_commissions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  lenderProductId: uuid("lender_product_id").references(() => lenderProducts.id, { onDelete: "cascade" }).notNull(),
  dealType: varchar("deal_type", { length: 50 }).notNull(),

  // Commission Rates
  upfrontRate: numeric("upfront_rate", { precision: 5, scale: 4 }).notNull(),
  trailRate: numeric("trail_rate", { precision: 5, scale: 4 }).default("0.0000"),

  // Effective Period
  effectiveFrom: date("effective_from").notNull(),
  effectiveTo: date("effective_to"),

  notes: text("notes"),

  // Audit Fields
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// 7. Document Checklists Table (P3 - Lower Priority)
// NOTE: Schema aligned to match actual database structure (ISSUE-048 fix)
export const documentChecklists = pgTable("crm_x_document_checklists", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  checklistName: varchar("checklist_name", { length: 255 }).notNull(),
  description: text("description"),
  lenderId: uuid("lender_id").references(() => lenders.id, { onDelete: "cascade" }),
  lenderProductId: uuid("lender_product_id").references(() => lenderProducts.id, { onDelete: "cascade" }),
  dealType: varchar("deal_type", { length: 50 }),
  entityType: varchar("entity_type", { length: 50 }),
  isActive: integer("is_active").default(1),

  // Audit Fields
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// 8. Document Checklist Items Table (P3 - Lower Priority)
// NOTE: Schema aligned to match actual database structure (ISSUE-048 fix)
export const documentChecklistItems = pgTable("crm_x_document_checklist_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  checklistId: uuid("checklist_id").references(() => documentChecklists.id, { onDelete: "cascade" }).notNull(),
  documentName: varchar("document_name", { length: 255 }).notNull(),
  documentCategory: varchar("document_category", { length: 100 }),
  description: text("description"),
  isRequired: integer("is_required").default(1),
  requiredForStage: varchar("required_for_stage", { length: 50 }),
  maxAgeMonths: integer("max_age_months"),
  acceptedFormats: text("accepted_formats").array(),
  sortOrder: integer("sort_order").default(0),

  // Audit Fields
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

// 9. Document Checklist Products Junction Table (ISSUE-069)
// Junction table to enable many-to-many relationship between checklists and products
export const documentChecklistProducts = pgTable("crm_x_document_checklist_products", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  checklistId: uuid("checklist_id").references(() => documentChecklists.id, { onDelete: "cascade" }).notNull(),
  lenderProductId: uuid("lender_product_id").references(() => lenderProducts.id, { onDelete: "cascade" }).notNull(),

  // Audit Fields
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// 10. Lender Document Requirements Table (SPRINT-28 F009)
// Maps required documents to lender/product combinations
export const lenderDocumentRequirements = pgTable("crm_x_lender_document_requirements", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  lenderId: uuid("lender_id").references(() => lenders.id, { onDelete: "cascade" }).notNull(),
  productTypeId: uuid("product_type_id").references(() => lenderProducts.id, { onDelete: "set null" }),
  // document_type_id FK enforced in migration (cross-domain — avoids circular import)
  documentTypeId: uuid("document_type_id").notNull(),

  requiredStage: varchar("required_stage", { length: 50 }).notNull().default("application"),
  isMandatory: boolean("is_mandatory").notNull().default(true),
  notes: text("notes"),

  // Multi-tenancy
  organisationId: uuid("organisation_id"),

  // Audit fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// Zod Schemas for Lender Manager
export const insertLenderSchema = createInsertSchema(lenders, {
  // Add explicit validation for required fields
  lenderName: z.string().min(1, "Lender name is required"),
  lenderCode: z.string().min(1, "Lender code is required"),
});
export const selectLenderSchema = createSelectSchema(lenders);

// Base schema for lender products (without cross-field validation)
// Used for .partial() in update schema - ZodEffects doesn't have .partial()
const baseLenderProductSchema = createInsertSchema(lenderProducts).extend({
  minTermMonths: z.number().min(0, "Minimum term cannot be negative").max(360, "Minimum term cannot exceed 360 months").optional(),
  maxTermMonths: z.number().min(0, "Maximum term cannot be negative").max(360, "Maximum term cannot exceed 360 months").optional(),
});

// Insert schema with cross-field validation
export const insertLenderProductSchema = baseLenderProductSchema.refine(
  (data) => {
    // Cross-field validation: max must be >= min (if both provided)
    if (data.minTermMonths !== undefined && data.maxTermMonths !== undefined) {
      return data.maxTermMonths >= data.minTermMonths;
    }
    return true;
  },
  {
    message: "Maximum term must be greater than or equal to minimum term",
    path: ["maxTermMonths"], // Show error on maxTermMonths field
  }
);
export const selectLenderProductSchema = createSelectSchema(lenderProducts);
export const insertLenderPricingTierSchema = createInsertSchema(lenderPricingTiers);
export const selectLenderPricingTierSchema = createSelectSchema(lenderPricingTiers);
export const insertLenderFeeGroupSchema = createInsertSchema(lenderFeeGroups);
export const selectLenderFeeGroupSchema = createSelectSchema(lenderFeeGroups);
export const insertLenderFeeSchema = createInsertSchema(lenderFees);
export const selectLenderFeeSchema = createSelectSchema(lenderFees);
export const insertLenderCommissionSchema = createInsertSchema(lenderCommissions);
export const selectLenderCommissionSchema = createSelectSchema(lenderCommissions);
export const insertDocumentChecklistSchema = createInsertSchema(documentChecklists);
export const selectDocumentChecklistSchema = createSelectSchema(documentChecklists);
export const insertDocumentChecklistItemSchema = createInsertSchema(documentChecklistItems);
export const selectDocumentChecklistItemSchema = createSelectSchema(documentChecklistItems);
export const insertDocumentChecklistProductSchema = createInsertSchema(documentChecklistProducts);
export const selectDocumentChecklistProductSchema = createSelectSchema(documentChecklistProducts);
export const insertLenderDocumentRequirementSchema = createInsertSchema(lenderDocumentRequirements, {
  lenderId: z.string().uuid(),
  documentTypeId: z.string().uuid(),
  productTypeId: z.string().uuid().optional().nullable(),
  requiredStage: z.string().min(1).max(50).default("application"),
  isMandatory: z.boolean().default(true),
});
export const selectLenderDocumentRequirementSchema = createSelectSchema(lenderDocumentRequirements);

// Update Schemas for Lender Manager
export const updateLenderSchema = insertLenderSchema.partial();
export const updateLenderProductSchema = baseLenderProductSchema.partial(); // Use base schema (ZodEffects doesn't have .partial())
export const updateLenderPricingTierSchema = insertLenderPricingTierSchema.partial();
export const updateLenderFeeGroupSchema = insertLenderFeeGroupSchema.partial();
export const updateLenderFeeSchema = insertLenderFeeSchema.partial();
export const updateLenderCommissionSchema = insertLenderCommissionSchema.partial();
export const updateDocumentChecklistSchema = insertDocumentChecklistSchema.partial();
export const updateDocumentChecklistItemSchema = insertDocumentChecklistItemSchema.partial();
export const updateDocumentChecklistProductSchema = insertDocumentChecklistProductSchema.partial();
export const updateLenderDocumentRequirementSchema = insertLenderDocumentRequirementSchema.partial();

// TypeScript Types for Lender Manager
export type Lender = typeof lenders.$inferSelect;
export type InsertLender = typeof lenders.$inferInsert;
export type LenderProduct = typeof lenderProducts.$inferSelect;
export type InsertLenderProduct = typeof lenderProducts.$inferInsert;
export type LenderPricingTier = typeof lenderPricingTiers.$inferSelect;
export type InsertLenderPricingTier = typeof lenderPricingTiers.$inferInsert;
export type LenderFeeGroup = typeof lenderFeeGroups.$inferSelect;
export type InsertLenderFeeGroup = typeof lenderFeeGroups.$inferInsert;
export type LenderFee = typeof lenderFees.$inferSelect;
export type InsertLenderFee = typeof lenderFees.$inferInsert;
export type LenderCommission = typeof lenderCommissions.$inferSelect;
export type InsertLenderCommission = typeof lenderCommissions.$inferInsert;
export type DocumentChecklist = typeof documentChecklists.$inferSelect;
export type InsertDocumentChecklist = typeof documentChecklists.$inferInsert;
export type DocumentChecklistItem = typeof documentChecklistItems.$inferSelect;
export type InsertDocumentChecklistItem = typeof documentChecklistItems.$inferInsert;
export type DocumentChecklistProduct = typeof documentChecklistProducts.$inferSelect;
export type InsertDocumentChecklistProduct = typeof documentChecklistProducts.$inferInsert;
export type LenderDocumentRequirement = typeof lenderDocumentRequirements.$inferSelect;
export type InsertLenderDocumentRequirement = typeof lenderDocumentRequirements.$inferInsert;
